import React from 'react';
import { Calendar } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { getAge } from '../../../utils/date';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { budgetCountries } from '../../../data/budget-countries';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ProfileHeroProps {
  profile: TravelProfile;
}

export function ProfileHero({ profile }: ProfileHeroProps) {
  const [cityPhoto, setCityPhoto] = React.useState<string | null>(null);
  
  // Get destination country info
  const countryInfo = React.useMemo(() => {
    if (!profile.preferences.destination) return { flag: null, name: "destination inconnue" };
    const country = budgetCountries.find(c => c.code === profile.preferences.destination);
    return {
      flag: country?.flag || null,
      name: country?.name || "destination inconnue"
    };
  }, [profile.preferences.destination]);

  // Fetch cached photo when profile loads
  React.useEffect(() => {
    const fetchCachedPhoto = async () => {
      if (profile.preferences.city) {
        try {
          const cacheRef = doc(db, 'photo_cache', profile.preferences.city.toLowerCase());
          const cacheDoc = await getDoc(cacheRef);
          
          if (cacheDoc.exists()) {
            const data = cacheDoc.data();
            const photos = data.photos;
            if (photos && photos.length > 0) {
              // Sélectionner une photo aléatoire du cache
              const randomPhoto = photos[Math.floor(Math.random() * photos.length)];
              setCityPhoto(randomPhoto.url);
            }
          }
        } catch (error) {
          console.error('Error fetching cached photo:', error);
        }
      }
    };

    fetchCachedPhoto();
  }, [profile.preferences.city]);

  // Format departure date
  const formattedDepartureDate = React.useMemo(() => {
    if (!profile.preferences.startDate) return null;
    try {
      const date = profile.preferences.startDate.toDate?.() || new Date(profile.preferences.startDate);
      return format(date, 'MMMM yyyy', { locale: fr });
    } catch (error) {
      return null;
    }
  }, [profile.preferences.startDate]);

  return (
    <div className="relative h-[320px] sm:h-[500px] overflow-hidden">
      {/* Overlay circles for visual effect */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary-500/10 dark:bg-primary-500/5 rounded-full blur-3xl z-0"></div>
      <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-accent-500/10 dark:bg-accent-500/5 rounded-full blur-3xl z-0"></div>
      
      {/* Background with blur effect */}
      <div className="absolute inset-0">
        {profile.photos && profile.photos.length > 0 ? (
          <img
            src={cityPhoto || getDestinationImage(profile.preferences.destination)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-md scale-110 opacity-25 dark:opacity-20 transition-opacity duration-300"
          />
        ) : cityPhoto ? (
          <img
            src={cityPhoto}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-md scale-110 opacity-25 dark:opacity-20"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-primary-500/5 to-accent-500/5 dark:from-primary-500/10 dark:to-accent-500/10"></div>
        )}
      </div>
      
      <div className="relative h-full flex items-center">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/50 to-transparent"></div>
        
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center md:items-end md:flex-row md:gap-10 relative z-10">
            {/* Profile Photo */}
            <div className="relative z-10 mt-8 sm:mt-0">
              <div className="relative w-28 sm:w-56 h-28 sm:h-56 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl bg-white dark:bg-gray-800">
                <img
                  src={cityPhoto || getDestinationImage(profile.preferences.destination)}
                  alt={profile.preferences.city}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left z-10 pb-6 pt-3 md:pt-0">
              <h1 className="text-lg sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3">
                {profile.firstName}, {getAge(profile.birthDate)} ans
              </h1>
              
              {formattedDepartureDate && (
                <div className="flex items-center justify-center md:justify-start gap-1.5 text-white/90">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="text-xs sm:text-sm">
                    Départ prévu : {formattedDepartureDate}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to get destination image
const getDestinationImage = (countryCode: string): string => {
  if (!countryCode) return 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
  
  const countryImages: Record<string, string> = {
    'aus': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=2670&auto=format&fit=crop',
    'nz': 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?q=80&w=2670&auto=format&fit=crop',
    'ca': 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?q=80&w=2670&auto=format&fit=crop',
    'jpn': 'https://images.unsplash.com/photo-1480796927426-f609979314bd?q=80&w=2670&auto=format&fit=crop',
    'kor': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?q=80&w=2670&auto=format&fit=crop',
    'twn': 'https://images.unsplash.com/photo-1470004914212-05527e49370b?q=80&w=2670&auto=format&fit=crop',
    'arg': 'https://images.unsplash.com/photo-1612294037637-ec328d0e075e?q=80&w=2670&auto=format&fit=crop',
    'chl': 'https://images.unsplash.com/photo-1546800273-4d63ff843853?q=80&w=2670&auto=format&fit=crop',
    'col': 'https://images.unsplash.com/photo-1576018753502-1a55b66cc44a?q=80&w=2670&auto=format&fit=crop',
    'mex': 'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?q=80&w=2670&auto=format&fit=crop',
    'per': 'https://images.unsplash.com/photo-1526392060635-9d6019884377?q=80&w=2670&auto=format&fit=crop',
    'uru': 'https://images.unsplash.com/photo-1624416254374-b471bfcaac81?q=80&w=2670&auto=format&fit=crop',
    'bra': 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?q=80&w=2670&auto=format&fit=crop',
    'hkg': 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?q=80&w=2670&auto=format&fit=crop',
    'fra': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=2670&auto=format&fit=crop',
    'ecu': 'https://images.unsplash.com/photo-1504457047772-27faf1c00561?q=80&w=2670&auto=format&fit=crop'
  };
  
  return countryImages[countryCode] || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
};